import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
	Box,
	Button,
	Grid,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import DateInput from "../components/Inputs/Date";
import { useItems } from "../features/Fetch/Items";
import {
	BudgetItem,
	BudgetType,
	CurrentUser,
	DateTime,
	SubBudgetItem,
	TimeframeItem,
	TransactionItem,
	USCurrency,
} from "../types";
import { setUseModalSpace } from "../features/Slices/modalSpaceSlice";
import Toggle from "../components/Inputs/Toggle";

type ProRataLine = {
	budget: BudgetItem;
	subBudget: SubBudgetItem | null;
	label: string;
	annualBudget: number | null;
	actualByMonth: number[];
	finalByMonth: number[];
	annualized: number;
};

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const EMPTY_TIMEFRAMES = [] as TimeframeItem[];
const ProRataReport = () => {
	const repo = useItems();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const user: CurrentUser = useSelector((s: any) => s.userData.value);
	const timeframes: TimeframeItem[] = useSelector((s: any) => s.timeframes.value) ?? EMPTY_TIMEFRAMES;
	const budgets: BudgetItem[] = useMemo(() => repo.GetBudgetTree() ?? [], [repo]);
	const transactions: TransactionItem[] | null = repo.SortedTransactions(null, true);

	const [reportDate, setReportDate] = useState<DateTime>(new DateTime());
	const [useProRata, setUseProRata] = useState<boolean>(false);

	const reportYear = reportDate.Value.getFullYear();
	const reportMonthIndex = reportDate.Value.getMonth(); // 0-11

	const { incomeLines, expenseLines, combinedLines, totals } = useMemo(() => {
		const yearStart = dayjs(new Date(reportYear, 0, 1));
		const yearEnd = dayjs(new Date(reportYear, 11, 31, 23, 59, 59, 999));
		const filteredTransactions = (transactions ?? [])
			.filter(t => {
				const d = dayjs(t.transactionDate.Value);
				return d.valueOf() >= yearStart.valueOf() && d.valueOf() <= yearEnd.valueOf();
			});

		const timeframesById = new Map<number, TimeframeItem>();
		timeframes.forEach(t => timeframesById.set(t.timeframeId, t));

		// Create base lines from budget structure
		const baseLines: ProRataLine[] = [];
		budgets
			.filter(b => b.budgetId !== 0)
			.filter(b => !b.isInactive)
			.forEach(budget => {
				const budgetPeriodsInYear = getAnnualFactor(budget);
				const subBudgets = (budget.subBudgets ?? []).filter(sb => !sb.isInactive);

				const budgetPeriodAmount = budget.variant?.amount ?? 0;
				baseLines.push({
					budget,
					subBudget: null,
					label: budget.description,
					annualBudget: budgetPeriodAmount * budgetPeriodsInYear,
					actualByMonth: Array(12).fill(0),
					finalByMonth: Array(12).fill(0),
					annualized: 0,
				});

				if (subBudgets.length > 0) {
					subBudgets
						.sort((a, b) => a.description.localeCompare(b.description))
						.forEach(sb => {
							const subBudgetPeriodAmount = sb.variant?.amount ?? 0;
							baseLines.push({
								budget,
								subBudget: sb,
								label: sb.description,
								annualBudget: subBudgetPeriodAmount * budgetPeriodsInYear,
								actualByMonth: Array(12).fill(0),
								finalByMonth: Array(12).fill(0),
								annualized: 0,
							});
						});
				}
			});

		// Index for fast month aggregation
		const lineByKey = new Map<string, ProRataLine>();
		for (const line of baseLines) {
			lineByKey.set(lineKey(line.budget.budgetId, line.subBudget?.subBudgetId), line);
		}

		// Fill actuals
		for (const t of filteredTransactions) {
			const month = dayjs(t.transactionDate.Value).month();
			for (const a of t.allocations) {
				const budgetKey = lineKey(a.budgetId, 0);
				const subBudgetKey = lineKey(a.budgetId, a.subBudgetId);
				const budgetLine = lineByKey.get(budgetKey);
				const subBudgetLine = lineByKey.get(subBudgetKey);
				if (!budgetLine) continue;
				budgetLine.actualByMonth[month] += a.amount;
				if (!subBudgetLine) continue;
				subBudgetLine.actualByMonth[month] += a.amount;
			}
		}

		// Apply pro-rata logic: finalized months use actuals, remaining months split evenly
		// Or, if disabled, show actuals only.
		for (const line of baseLines) {
			if (!useProRata) {
				line.finalByMonth = [...line.actualByMonth];
				line.annualized = line.finalByMonth.reduce((s, v) => s + v, 0);
				continue;
			}

			const annualBudget = line.annualBudget;
			if (annualBudget == null) {
				line.finalByMonth = [...line.actualByMonth];
				line.annualized = line.finalByMonth.reduce((s, v) => s + v, 0);
				continue;
			}

			const reportMonthActual = line.actualByMonth[reportMonthIndex];
			let finalizedActual = line.actualByMonth.slice(0, reportMonthIndex).reduce((s, v) => s + v, 0);
			let remainingMonths = 12 - reportMonthIndex;
			let remainingBudget = annualBudget - finalizedActual;
			let plannedPerMonth = remainingMonths > 0 ? remainingBudget / remainingMonths : 0;
			const isCurrentMonthActual = reportMonthActual > plannedPerMonth;
			if (isCurrentMonthActual) {
				finalizedActual += reportMonthActual;
				remainingMonths--;
				remainingBudget = annualBudget - finalizedActual;
				plannedPerMonth = remainingMonths > 0 ? remainingBudget / remainingMonths : 0;
			}

			for (let i = 0; i < 12; i++) {
				line.finalByMonth[i] = (i < reportMonthIndex || (i === reportMonthIndex && isCurrentMonthActual)) ? line.actualByMonth[i] : plannedPerMonth;
			}
			line.annualized = line.finalByMonth.reduce((s, v) => s + v, 0);
		}

		const incomeLines = baseLines
			.filter(l => l.budget.budgetType === BudgetType.Income)
			.sort((a, b) => a.budget.description.localeCompare(b.budget.description));
		const expenseLines = baseLines
			.filter(l => l.budget.budgetType === BudgetType.Expense)
			.sort((a, b) => a.budget.description.localeCompare(b.budget.description));
		const combinedLines = baseLines
			.filter(l => l.budget.budgetType === BudgetType.Combined)
			.sort((a, b) => a.budget.description.localeCompare(b.budget.description));

		const incomeTotals = sumMonths(incomeLines);
		const expenseTotals = sumMonths(expenseLines);
		const combinedTotals = sumMonths(combinedLines);
		const cashFlow = incomeTotals.map((v, i) => v - expenseTotals[i]);

		return {
			incomeLines,
			expenseLines,
			combinedLines,
			totals: {
				incomeTotals,
				expenseTotals,
				combinedTotals,
				cashFlow,
				incomeAnnualized: incomeTotals.reduce((s, v) => s + v, 0),
				expenseAnnualized: expenseTotals.reduce((s, v) => s + v, 0),
				combinedAnnualized: combinedTotals.reduce((s, v) => s + v, 0),
				cashFlowAnnualized: cashFlow.reduce((s, v) => s + v, 0),
			},
		};

		function getAnnualFactor(budget: BudgetItem) {
			const timeframeId = (budget.variant?.timeframeId ?? budget.timeframeId) as number | undefined;
			const timeframe = timeframeId ? timeframesById.get(timeframeId) : undefined;
			if (!timeframe) return 12; // safest default: assume monthly
			if ((timeframe.months ?? 0) > 0) return 12 / timeframe.months;
			if ((timeframe.days ?? 0) > 0) return 365 / timeframe.days;
			return 12;
		}

		function sumMonths(lines: ProRataLine[]) {
			const months = Array(12).fill(0);
			for (const l of lines) {
				if (!l.subBudget) continue;
				for (let i = 0; i < 12; i++) months[i] += l.finalByMonth[i];
			}
			return months;
		}
	}, [budgets, reportMonthIndex, reportYear, timeframes, transactions, useProRata]);

	useEffect(() => {
		dispatch(setUseModalSpace(false));
		return () => { dispatch(setUseModalSpace(true)); };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch]);

	return (
		<div className="container-fluid">
			<Grid container spacing={2} sx={{ mt: 1 }}>
				<Grid item xs={12}>
					<Paper sx={{ p: 1.5 }}>
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
							<Typography variant="h5" sx={{ fontWeight: "bold" }}>
								{reportYear} {user.familyName} Budget
							</Typography>
							<Box sx={{ display: 'flex', gap: 1 }}>
								<Button variant="outlined" onClick={handleExportCsv}>Export CSV</Button>
								<Button variant="outlined" color="secondary" onClick={() => navigate("/reports")}>Close</Button>
							</Box>
						</Box>

						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0, alignItems: "flex-start", mb: 2 }}>
							<DateInput Borderless Size={2} Label="Reporting Day" Value={reportDate} OnChange={(v: any) => setReportDate(new DateTime(v))} />
							<Toggle Size={3} Label="Pro Rata Remaining" Position="end" Checked={useProRata} OnChange={(e: any) => setUseProRata(!!e.target.checked)} />
						</Box>

						<Box sx={{ mb: 1 }}>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell sx={{ fontWeight: "bold" }}>Category</TableCell>
										{MONTH_LABELS.map(m => (
											<TableCell key={m} align="right" sx={{ fontWeight: "bold" }}>
												{m}
											</TableCell>
										))}
										<TableCell align="right" sx={{ fontWeight: "bold" }}>
											Annualized
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
								{incomeLines.length > 0 && <ProRataTable title="Offerings/Income" lines={incomeLines} showBudgetHeadings months={totals.incomeTotals} annualized={totals.incomeAnnualized} />}
								{combinedLines.length > 0 && <ProRataTable title="Combined" lines={combinedLines} showBudgetHeadings months={totals.combinedTotals} annualized={totals.combinedAnnualized} />}
								{expenseLines.length > 0 && <ProRataTable title="Expenses" lines={expenseLines} showBudgetHeadings months={totals.expenseTotals} annualized={totals.expenseAnnualized} />}
								<ProRataTotalsRow label="Cash Flow +/-" months={totals.cashFlow} annualized={totals.cashFlowAnnualized} emphasize />
								</TableBody>
								<Box sx={{ mt: 2 }} />
							</Table>
						</Box>
					</Paper>
				</Grid>
			</Grid>
		</div>
	);

	function handleExportCsv() {
		//const header = ["Category", ...MONTH_LABELS, "Annualized"];
		const rows: string[][] = [];
		//rows.push(header);

		if (incomeLines.length > 0) {
			//pushTotalsRow("Offerings/Income", totals.incomeTotals, totals.incomeAnnualized);
			pushLineRows(incomeLines.filter(l => l.subBudget));
		}

		if (combinedLines.length > 0) {
			//pushTotalsRow("Combined", totals.combinedTotals, totals.combinedAnnualized);
			pushLineRows(combinedLines);
		}

		if (expenseLines.length > 0) {
			//pushTotalsRow("Expenses", totals.expenseTotals, totals.expenseAnnualized);
			pushLineRows(expenseLines);
		}

		//pushTotalsRow("Cash Flow +/-", totals.cashFlow, totals.cashFlowAnnualized);

		const csv = rows.map(r => r.map(csvEscape).join(",")).join("\r\n") + "\r\n";
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

		const fileName = sanitizeFileName(`${reportYear} ${user.familyName} Budget${useProRata ? " ProRata" : " Actuals"}.csv`);
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", fileName);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);

		//function pushTotalsRow(label: string, months: number[], annualized: number) {
		//	rows.push([label, ...months.map(USCurrencyAccounting), USCurrencyAccounting(annualized)]);
		//}

		function pushLineRows(lines: ProRataLine[]) {
			let currentBudgetId: number | null = null;
			for (const line of lines) {
				const showBudget = currentBudgetId !== line.budget.budgetId;
				if (showBudget) currentBudgetId = line.budget.budgetId;
				// Keep label text as-is to match the rendered table; styling/indent is not represented in CSV.
				rows.push([line.label, ...line.finalByMonth.map(USCurrencyAccounting), USCurrencyAccounting(line.annualized)]);
			}
		}
	}
};

function lineKey(budgetId: number, subBudgetId: number | undefined) {
	return `${budgetId}:${subBudgetId ?? 0}`;
}

function USCurrencyAccounting(value: number) {
	// Display negatives as ($1,234.56) instead of -$1,234.56
	if (Object.is(value, -0)) value = 0;
	if (value < 0) return `(${USCurrency(Math.abs(value))})`;
	return USCurrency(value);
}

function csvEscape(value: string) {
	const s = value ?? "";
	if (/[",\r\n]/.test(s)) {
		return `"${s.replace(/"/g, '""')}"`;
	}
	return s;
}

function sanitizeFileName(name: string) {
	const cleaned = name.replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim();
	return cleaned.replace(/[.\s]+$/g, "") || "AnnualBudgetBreakdown.csv";
}

function ProRataTable(props: { title: string; lines: ProRataLine[]; showBudgetHeadings?: boolean, months: number[], annualized: number }) {
	const { title, lines, showBudgetHeadings, months, annualized } = props;

	let currentBudgetId: number | null = null;

	return (
		<>
			<ProRataTotalsRow label={title} months={months} annualized={annualized} emphasize />
			{lines.map(line => {
				const showBudget = !!showBudgetHeadings && currentBudgetId !== line.budget.budgetId;
				if (showBudget) currentBudgetId = line.budget.budgetId;
				if (showBudget && line.budget.budgetType === BudgetType.Income) return <></>;

				return (
					<React.Fragment key={lineKey(line.budget.budgetId, line.subBudget?.subBudgetId)}>
						<TableRow>
							<TableCell sx={{ pl: showBudget ? 5 : 1, fontWeight: showBudget ? "bold" : undefined }}>
								{line.label}
							</TableCell>
							{line.finalByMonth.map((v, i) => (
								<TableCell key={i} align="right" sx={{ fontWeight: showBudget ? "bold" : undefined }}>
									{USCurrencyAccounting(v)}
								</TableCell>
							))}
							<TableCell align="right" sx={{ fontWeight: showBudget ? "bold" : undefined }}>{USCurrencyAccounting(line.annualized)}</TableCell>
						</TableRow>
					</React.Fragment>
				);
			})}
	</>);
}

function ProRataTotalsRow(props: { label: string; months: number[]; annualized: number; emphasize?: boolean }) {
	const { label, months, annualized, emphasize } = props;
	return (
			<TableRow sx={{ fontWeight: "bold", bgcolor: "action.hover" }}>
				<TableCell sx={{ fontWeight: emphasize ? "bold" : undefined }}>{label}</TableCell>
				{months.map((v, i) => (
					<TableCell key={i} align="right" sx={{ fontWeight: emphasize ? "bold" : undefined }}>
						{USCurrencyAccounting(v)}
					</TableCell>
				))}
				<TableCell align="right" sx={{ fontWeight: emphasize ? "bold" : undefined }}>
					{USCurrencyAccounting(annualized)}
				</TableCell>
			</TableRow>
	);
}

export default ProRataReport;
